import React, { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Redirect } from 'react-router';
import styled, { css } from 'styled-components';
import { Form, Select, message, Collapse, Spin, Empty } from 'antd';
import { DragDropContext } from 'react-beautiful-dnd';
import FormTitle from '../components/auth/FormTitle';
import SettingsMenu from '../components/settings/SettingsMenu';
import Button from '../UI/buttons/Button';
import ReactGa from 'react-ga4';
import { useAppSelector } from '../store/hooks';
import { CLIENT_ROLE } from '../constants/roles';
import SelectInput from '../UI/inputs/Select';
import { CaretDownOutlined } from '@ant-design/icons';
import { getRemoteOrganizationSettings, updateRemoteOrganizationSettings, getCurrentRemoteOrganizationInfo, getSmartRecruitersRoles, getRecruiteeRoles, getDefaultPermissions } from '../api/remoteOrganization';
import { updateClientCompanySettings } from '../api/company';
import NotFound from './NotFound';
import axios from 'axios';
import CompanyDefaultJobExtensionFieldsForm from '../components/settings/CompanyDefaultJobExtensionFieldsForm';
import StageGroup from './AtsSettings/components/StageGroup';
import ActionToolbar from './AtsSettings/components/ActionToolbar';
import { useStageMapping } from './AtsSettings/hooks/useStageMapping';

const PageWrapper = styled.section`
  background: ${({ theme }) => theme.lozengeColor};
  width: 100%;
  display: flex;
  min-height: 100vh;
  flex-flow: row-reverse;
`;

// Add hideMenu prop to FormWrapper interface
interface FormWrapperProps {
    hideMenu?: boolean;
}

// Modify FormWrapper to use hideMenu prop for width
const FormWrapper = styled.div<FormWrapperProps>`
  background: #fff;
  width: ${({ hideMenu }) => (hideMenu ? '100%' : '61%')};
  padding: 4.5rem 4rem;
  display: flex;
  flex-direction: column;
  position: relative;

  @media (max-width: 1200px) {
    width: ${({ hideMenu }) => (hideMenu ? '100%' : '70%')};
  }
  @media (max-width: 768px) {
    width: ${({ hideMenu }) => (hideMenu ? '100%' : '90%')};
    padding: 3rem;
  }
`;

const StyledForm = styled(Form)`
  margin-top: 2.5rem;
  width: 100%;
`;

const PermissionSection = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  margin-bottom: 2rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid #dae5ec;
`;

const PermissionTextWrapper = styled.div`
  flex: 1;
  padding-right: 2rem;
`;

const PermissionTitle = styled.h3`
  font-size: 16px;
  font-weight: bold;
  margin-bottom: 0.25rem;
`;

const PermissionDescription = styled.p`
  font-size: 13px;
  color: #4a5568;
  margin-bottom: 0;
`;

const PermissionControls = styled.div`
  display: flex;
  gap: 0.5rem;
  flex-shrink: 0;
`;

const DropdownContainer = styled.div`
  display: flex;
  gap: 1rem;
  margin-bottom: 2.5rem;
`;

// Wrapper to make SelectInput flexible
const FlexSelectWrapper = styled(SelectInput)`
  flex: 1; // Make the select input take up equal space
`;

// Updated PermissionButton styled component
interface PermissionButtonProps {
    isActive?: boolean;
}

const PermissionButton = styled(Button) <PermissionButtonProps>`
  border-radius: 8px;
  padding: 0.3rem 1rem;
  min-width: 100px;
  text-align: center;
  transition: background-color 0.2s, color 0.2s, border-color 0.2s;
  height: 36px;
  line-height: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  font-size: 13px;

  ${({ isActive, theme }) =>
        isActive
            ? css`
          /* Active state: White background, green text/border */
          background-color: white;
          color: ${theme.primaryColor};
          border-color: ${theme.primaryColor};
          border-width: 1px;
          border-style: solid;

          &:hover,
          &:focus {
            background-color: ${theme.lozengeColorLight};
            color: ${theme.hoverColor};
            border-color: ${theme.hoverColor};
          }
        `
            : css`
          /* Inactive state: Light grey background and border */
          background-color: ${theme.lozengeColorLight};
          color: #627d95;
          border-color: #dae5ec;
          border-width: 1px;
          border-style: solid;

          &:hover,
          &:focus {
            color: ${theme.primaryColor};
            border-color: ${theme.primaryColor};
            background-color: white;
          }
        `}
`;

const SectionTitle = styled.h2`
  font-size: 1.5rem; // Larger title for section separator
  font-weight: 600;
  color: #1f2937; // Darker title color
  margin-top: 3rem; // Space above the title
  margin-bottom: 1.5rem; // Space below the title
`;

const SectionDescription = styled.p`
  font-size: 13px;
  color: #4a5568;
  margin-top: -1rem; // Adjust spacing if needed
  margin-bottom: 1.5rem;
`;

// Add styles for the Collapse component if needed, or use default
const StyledCollapse = styled(Collapse)`
  background-color: #fff; // Match form background
  border: none; // Remove default border
  margin-top: 3rem; // Space above the collapse section
  margin-bottom: 1.5rem; // Space below the collapse section

  .ant-collapse-item {
    border-bottom: 1px solid #f0f0f0; // Optional separator
  }

  .ant-collapse-header {
    font-size: 1.5rem; 
    font-weight: 600;
    color: #1f2937; 
    padding: 12px 0 !important; // Adjust padding
  }

  .ant-collapse-content {
    border-top: none;
    padding-bottom: 1rem; // Add padding below content
  }
  
  .ant-collapse-content-box {
      padding: 16px 0px; // Adjust padding
  }
`;

// Add styles for stage mapping components
const ControlsRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
  gap: 1rem;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
    gap: 0.5rem;
  }
`;

const SearchWrapper = styled.div`
  flex: 1;
  max-width: 400px;
  margin-bottom: 1rem;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 0.5rem 1rem;
  border: 1px solid #dae5ec;
  border-radius: 6px;
  font-size: 14px;
  
  &::placeholder {
    color: #8a94a6;
  }
  
  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.primaryColor};
  }
`;

const ButtonsContainer = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-top: 1rem;
  margin-bottom: 1rem;

  @media (max-width: 768px) {
    justify-content: stretch;
    
    > * {
      flex: 1;
    }
  }
`;

// Helper function to convert API permissions to UI permission levels
const mapPermissionsToUI = (permissions: any) => {
    const result = {
        masterContractLevel: 'None',
        agenciesLevel: 'None',
        chatLevel: 'None',
        invoicesLevel: 'None',
        analyticsLevel: 'None',
        compensationLevel: 'None',
        feeInfoLevel: 'None',
    };

    if (!permissions) return result;

    // Master Contracts - Manage now includes all permissions (including remove)
    if (permissions.view_mastercontracts && permissions.create_mastercontracts && permissions.edit_mastercontracts) {
        result.masterContractLevel = 'Manage';
    } else if (permissions.view_mastercontracts) {
        result.masterContractLevel = 'View';
    }

    // Agencies (Suppliers) - Manage now includes all permissions (including create/remove)
    if (permissions.view_suppliers && permissions.edit_suppliers) {
        result.agenciesLevel = 'Manage';
    } else if (permissions.view_suppliers) {
        result.agenciesLevel = 'Assign';
    }

    // Chat - Interact now includes all permissions
    if (permissions.view_chat && permissions.create_chat) {
        result.chatLevel = 'Interact';
    }

    // Invoices - Interact now includes all permissions (including edit)
    if (permissions.view_invoices && permissions.create_invoices) {
        result.invoicesLevel = 'Interact';
    }

    // Analytics
    if (permissions.view_analytics) {
        result.analyticsLevel = 'View';
    }

    // Compensation (Salary Info)
    if (permissions.view_salaryinfo) {
        result.compensationLevel = 'View';
    }

    // Fee Information
    if (permissions.view_feeinfo) {
        result.feeInfoLevel = 'View';
    }

    return result;
};

// NEW helper function to convert a single UI permission level change to its specific API permissions
const mapSingleUIToApiPermissions = (changedLevel: Partial<{
    masterContractLevel: string;
    agenciesLevel: string;
    chatLevel: string;
    invoicesLevel: string;
    analyticsLevel: string;
    compensationLevel: string;
    feeInfoLevel: string;
}>): any => { // Using 'any' for return type as ApiPermissionSet is not defined in current context
    const apiPartial: any = {};
    const [levelKey, levelValue] = Object.entries(changedLevel)[0];

    if (levelKey === 'masterContractLevel') {
        if (levelValue === 'None') {
            apiPartial.view_mastercontracts = false;
            apiPartial.create_mastercontracts = false;
            apiPartial.edit_mastercontracts = false;
            apiPartial.remove_mastercontracts = false;
        } else if (levelValue === 'View') {
            apiPartial.view_mastercontracts = true;
            apiPartial.create_mastercontracts = false;
            apiPartial.edit_mastercontracts = false;
            apiPartial.remove_mastercontracts = false;
        } else if (levelValue === 'Manage') {
            apiPartial.view_mastercontracts = true;
            apiPartial.create_mastercontracts = true;
            apiPartial.edit_mastercontracts = true;
            apiPartial.remove_mastercontracts = true; // Manage now includes all permissions
        }
    } else if (levelKey === 'agenciesLevel') {
        if (levelValue === 'None') {
            apiPartial.view_suppliers = false;
            apiPartial.edit_suppliers = false;
            apiPartial.create_suppliers = false;
            apiPartial.remove_suppliers = false;
        } else if (levelValue === 'Assign') {
            apiPartial.view_suppliers = true;
            apiPartial.edit_suppliers = false;
            apiPartial.create_suppliers = false;
            apiPartial.remove_suppliers = false;
        } else if (levelValue === 'Manage') {
            apiPartial.view_suppliers = true;
            apiPartial.edit_suppliers = true;
            apiPartial.create_suppliers = true; // Manage now includes all permissions
            apiPartial.remove_suppliers = true; // Manage now includes all permissions
        }
    } else if (levelKey === 'chatLevel') {
        if (levelValue === 'None') {
            apiPartial.view_chat = false;
            apiPartial.create_chat = false;
        } else if (levelValue === 'Interact') {
            apiPartial.view_chat = true;
            apiPartial.create_chat = true;
        }
    } else if (levelKey === 'invoicesLevel') {
        if (levelValue === 'None') {
            apiPartial.view_invoices = false;
            apiPartial.create_invoices = false;
            apiPartial.edit_invoices = false;
        } else if (levelValue === 'Interact') {
            apiPartial.view_invoices = true;
            apiPartial.create_invoices = true;
            apiPartial.edit_invoices = true; // Interact now includes all permissions
        }
    } else if (levelKey === 'analyticsLevel') {
        if (levelValue === 'None') {
            apiPartial.view_analytics = false;
        } else if (levelValue === 'View') {
            apiPartial.view_analytics = true;
        }
    } else if (levelKey === 'compensationLevel') {
        if (levelValue === 'None') {
            apiPartial.view_salaryinfo = false;
        } else if (levelValue === 'View') {
            apiPartial.view_salaryinfo = true;
        }
    } else if (levelKey === 'feeInfoLevel') {
        if (levelValue === 'None') {
            apiPartial.view_feeinfo = false;
        } else if (levelValue === 'View') {
            apiPartial.view_feeinfo = true;
        }
    }
    return apiPartial;
};

// Add prop type for hideMenu
interface PermissionsSettingsProps {
    hideMenu?: boolean;
}

// Update component to accept props
const PermissionsSettings: React.FC<PermissionsSettingsProps> = ({ hideMenu }) => {
    const { t } = useTranslation();
    const [form] = Form.useForm();
    const user = useAppSelector((state) => state.user.user);

    // Use edit_settings for authorization check for managing defaults
    const canManageSettings = user?.role === CLIENT_ROLE && user?.permissions?.edit_settings === true;
    const canViewSettings = user?.role === CLIENT_ROLE && user?.permissions?.view_settings === true;

    // Add state for the selected role
    const [selectedRole, setSelectedRole] = useState<string | null>(null);

    // Add state for organization data
    const [orgData, setOrgData] = useState<any>(null);
    const [loading, setLoading] = useState(false);

    // Add state for button selection
    const [masterContractLevel, setMasterContractLevel] = useState('None');
    const [agenciesLevel, setAgenciesLevel] = useState('None');
    const [chatLevel, setChatLevel] = useState('None');

    // Get the current remote organization info from user
    const [atsId, setAtsId] = useState<string>('');
    const [organizationId, setOrganizationId] = useState<string>('');

    // Add state for orgNotFound
    const [orgNotFound, setOrgNotFound] = useState(false);

    // Add state for new general settings
    const [duplicateCandidatePreventionLevel, setDuplicateCandidatePreventionLevel] = useState('None'); // Options: None, Job, Company

    // State for dynamic roles (no SYSTEM_ROLES)
    const [roles, setRoles] = useState<{ id: string, name: string }[]>([]);
    // Track if roles have been loaded for this org
    const [rolesLoaded, setRolesLoaded] = useState(false);

    // Add state for default permissions
    const [defaultPermissions, setDefaultPermissions] = useState<any>(null);

    // Stage mapping functionality - only initialize if user has permission
    const canSetDefaultStageMappings = user?.company?.can_set_default_stage_mappings;
    const stageMappingResult = useStageMapping();

    // Fetch default permissions on mount
    useEffect(() => {
        getDefaultPermissions().then(res => {
            setDefaultPermissions(res.data);
        });
    }, []);

    // Wrap handleRoleChange in useCallback for stable reference
    const handleRoleChange = useCallback((value: string) => {
        setSelectedRole(value);
        if (orgData && orgData.extended_data && orgData.extended_data.role_permissions && orgData.extended_data.role_permissions[value]) {
            const rolePermissions = orgData.extended_data.role_permissions[value] || {};
            const uiPermissions = mapPermissionsToUI(rolePermissions);
            setMasterContractLevel(uiPermissions.masterContractLevel);
            setAgenciesLevel(uiPermissions.agenciesLevel);
            setChatLevel(uiPermissions.chatLevel);
        } else if (defaultPermissions) {
            // Use backend defaults if no custom permissions
            // Find the role name from the roles array using the ID
            const selectedRoleObj = roles.find(role => role.id.toString() === value.toString());
            const roleName = selectedRoleObj ? selectedRoleObj.name.toLowerCase() : '';

            let perms;
            if (roleName === 'administrator' || roleName === 'admin') {
                perms = defaultPermissions.admin;
            } else {
                perms = defaultPermissions.standard;
            }
            const uiPermissions = mapPermissionsToUI(perms);
            setMasterContractLevel(uiPermissions.masterContractLevel);
            setAgenciesLevel(uiPermissions.agenciesLevel);
            setChatLevel(uiPermissions.chatLevel);
        } else {
            resetDefaults();
        }
    }, [orgData, defaultPermissions, roles]);

    // Also update the effect dependencies to add 't' to prevent linter warnings
    useEffect(() => {
        if (canViewSettings) {
            const loadSettings = async () => {
                try {
                    setLoading(true);

                    // Step 1: First get remote organization info (this provides atsId and organizationId)
                    const orgInfoResponse = await getCurrentRemoteOrganizationInfo();

                    if (orgInfoResponse.data && orgInfoResponse.data.ats_id && orgInfoResponse.data.organization_id) {
                        // Set the IDs for later use
                        const atsId = orgInfoResponse.data.ats_id;
                        const orgId = orgInfoResponse.data.organization_id;
                        setAtsId(atsId);
                        setOrganizationId(orgId);

                        // Step 2: Get organization settings (this contains permissions in extended_data)
                        const orgSettingsResponse = await getRemoteOrganizationSettings(atsId, orgId);
                        setOrgData(orgSettingsResponse.data);

                        // Extract any existing settings from extended_data
                        const ext = orgSettingsResponse.data?.extended_data || {};
                        setDuplicateCandidatePreventionLevel(ext.duplicate_candidate_prevention_level || 'None');

                        // Step 3: Get client company settings (contains the actual settings values)
                        const clientCompanyResponse = await axios.get(`${process.env.REACT_APP_API_URL}client-companies/current/`);

                        // Handle duplicate candidate prevention setting
                        if (clientCompanyResponse.data.duplicate_candidate_months === 0) {
                            setDuplicateCandidatePreventionLevel('None');
                        } else if (clientCompanyResponse.data.duplicate_candidate_prevention_level) {
                            const level = clientCompanyResponse.data.duplicate_candidate_prevention_level;
                            if (level === 'job') {
                                setDuplicateCandidatePreventionLevel('Job');
                            } else if (level === 'company') {
                                setDuplicateCandidatePreventionLevel('Company');
                            } else if (level === 'supercompany') {
                                setDuplicateCandidatePreventionLevel('Supercompany');
                            } else {
                                setDuplicateCandidatePreventionLevel('None');
                            }
                        } else {
                            setDuplicateCandidatePreventionLevel('None');
                        }
                    } else {
                        setOrgNotFound(true);
                    }
                    setLoading(false);
                } catch (error) {
                    console.error('Error loading settings:', error);
                    setOrgNotFound(true);
                    setLoading(false);
                    message.error(t('ERROR_FETCHING_ORGANIZATION_SETTINGS'));
                }
            };

            loadSettings();
        }
    }, [canViewSettings, t]);

    // In useEffect for fetching ATS roles, detect ATS type and call appropriate API
    useEffect(() => {
        if (orgData && atsId && organizationId && !rolesLoaded && (defaultPermissions || atsId === 'jobylon')) {
            // --- Jobylon: use a predefined role list instead of remote API ---
            if (atsId === 'jobylon') {
                const jobylonRoles = [
                    { id: 'manager', name: 'Manager' },
                    { id: 'member', name: 'Member' },
                ];

                setRoles(jobylonRoles);
                setRolesLoaded(true);

                // Auto-select the first role to keep UI behaviour consistent
                if (jobylonRoles.length > 0) {
                    const firstRoleId = jobylonRoles[0].id.toString();
                    setSelectedRole(firstRoleId);
                    handleRoleChange(firstRoleId);
                } else {
                    setSelectedRole(null);
                }

                return; // Skip remote roles fetch
            }
            // Determine which roles API to call based on atsId
            const getRolesFunction = atsId === 'recruitee' ? getRecruiteeRoles : getSmartRecruitersRoles;

            getRolesFunction(atsId, organizationId)
                .then(res => {
                    const apiRoles = Array.isArray(res.data) ? res.data : [];
                    const filteredRoles = apiRoles.filter(role =>
                        !['ADMIN', 'ADMINISTRATOR'].includes(role.name.toUpperCase())
                    );

                    setRoles(filteredRoles); // Set the filtered roles to state for rendering
                    setRolesLoaded(true);
                    if (filteredRoles.length > 0) {
                        const firstRoleId = filteredRoles[0].id.toString();
                        setSelectedRole(firstRoleId);
                        handleRoleChange(firstRoleId); // Ensure handleRoleChange is called with the ID of the first filtered role
                    } else {
                        setSelectedRole(null);
                        // Optionally, call handleRoleChange(null) or resetDefaults() if no roles are available
                        // resetDefaults(); // Or handleRoleChange(null) if that's more appropriate
                    }
                })
                .catch(error => {
                    console.error('Error fetching roles:', error);
                    setRoles([]);
                    setRolesLoaded(true);
                    setSelectedRole(null);
                    // resetDefaults(); // Or handleRoleChange(null)
                });
        }
    }, [orgData, atsId, organizationId, rolesLoaded, handleRoleChange, defaultPermissions]);

    // Reset rolesLoaded if org changes (not on every orgData update)
    useEffect(() => {
        setRolesLoaded(false);
    }, [atsId, organizationId]);

    useEffect(() => {
        ReactGa.pageview('/permissions-settings');
    }, []);

    // Save permissions immediately on any change
    const savePermissions = async (nextState: Partial<{
        masterContractLevel: string;
        agenciesLevel: string;
        chatLevel: string;
        invoicesLevel: string;
        analyticsLevel: string;
        compensationLevel: string;
        feeInfoLevel: string;
    }>) => {
        if (!selectedRole) return;
        try {
            // Use the new helper to get only permissions for the changed category
            const focusedApiPermissions = mapSingleUIToApiPermissions(nextState);

            // Construct the payload: only the role_permissions for the selected role,
            // and within that, only the focused API permissions derived from the single UI change.
            const extendedDataPayload = {
                role_permissions: {
                    [selectedRole]: focusedApiPermissions
                }
            };

            await updateRemoteOrganizationSettings(atsId, organizationId, extendedDataPayload);

            // Refresh orgData to get the latest state, including defaults applied by backend for other categories.
            // This will also trigger handleRoleChange to update the UI state based on the new data.
            const orgSettingsResponse = await getRemoteOrganizationSettings(atsId, organizationId);
            setOrgData(orgSettingsResponse.data);

            message.success(t('PERMISSIONS_UPDATED_SUCCESSFULLY'));
        } catch (error) {
            message.error(t('ERROR_SAVING_PERMISSIONS'));
        }
    };

    const resetDefaults = () => {
        setMasterContractLevel('None');
        setAgenciesLevel('None');
        setChatLevel('None');
        setDuplicateCandidatePreventionLevel('None');
    };

    // Add handlers for updating settings directly
    const handleDuplicateCandidatePreventionChange = useCallback((value: string) => {
        setDuplicateCandidatePreventionLevel(value);

        // Map UI values to backend values
        let backendValue;
        let months;
        if (value === 'None') {
            backendValue = 'job'; // Default to job level if None
            months = 0; // Disable duplicate candidate prevention
        } else if (value === 'Job') {
            backendValue = 'job';
            months = 12;
        } else if (value === 'Company') {
            backendValue = 'company';
            months = 12;
        } else if (value === 'Supercompany') {
            backendValue = 'supercompany';
            months = 12;
        } else {
            backendValue = 'job'; // Default fallback
            months = 12;
        }

        // Send update directly to backend using token authentication
        updateClientCompanySettings({
            duplicate_candidate_prevention_level: backendValue,
            duplicate_candidate_months: months
        }).then(() => {
            message.success(t('SETTINGS_UPDATED_SUCCESSFULLY'));
        }).catch(error => {
            console.error('Error updating duplicate candidate prevention level:', error);
            message.error(t('ERROR_UPDATING_SETTINGS'));
        });
    }, [t]);

    if (!canViewSettings && !canManageSettings) {
        return <NotFound message={t('NO_PERMISSION')} />;
    }

    if (loading) {
        return (
            <PageWrapper>
                {/* Pass hideMenu prop to FormWrapper */}
                <FormWrapper hideMenu={hideMenu}>
                    {!hideMenu && <SettingsMenu menu="permissions" />}
                    <FormTitle text={t('LOADING_PERMISSIONS')} />
                </FormWrapper>
            </PageWrapper>
        );
    }

    if (orgNotFound) {
        return <Redirect to="/notfound" />;
    }

    return (
        <PageWrapper>
            {/* Pass hideMenu prop to FormWrapper */}
            <FormWrapper hideMenu={hideMenu}>
                {!hideMenu && <SettingsMenu menu="permissions" />}
                <FormTitle text={t('PORTAL_SETTINGS')} />

                {/* General Settings Section (formerly Notifications) */}
                <SectionTitle>{t('GENERAL_SETTINGS')}</SectionTitle>
                <PermissionSection>
                    <PermissionTextWrapper>
                        <PermissionTitle>{t('DUPLICATE_CANDIDATE_PREVENTION')}</PermissionTitle>
                        <PermissionDescription>
                            {t('DUPLICATE_CANDIDATE_PREVENTION_DESC')}
                        </PermissionDescription>
                    </PermissionTextWrapper>
                    <PermissionControls>
                        <PermissionButton
                            isActive={duplicateCandidatePreventionLevel === 'None'}
                            onClick={function () {
                                handleDuplicateCandidatePreventionChange('None');
                            }}
                        >{t('NONE')}</PermissionButton>
                        <PermissionButton
                            isActive={duplicateCandidatePreventionLevel === 'Job'}
                            onClick={function () {
                                handleDuplicateCandidatePreventionChange('Job');
                            }}
                        >{t('JOB')}</PermissionButton>
                        <PermissionButton
                            isActive={duplicateCandidatePreventionLevel === 'Company'}
                            onClick={function () {
                                handleDuplicateCandidatePreventionChange('Company');
                            }}
                        >{t('COMPANY_WIDE')}</PermissionButton>
                    </PermissionControls>
                </PermissionSection>

                {/* --- Default Job Questions Section (New Accordion) --- */}
                {canManageSettings && organizationId && user?.company?.enableJobExtensionFields && (
                    <StyledCollapse bordered={false} ghost defaultActiveKey={['1']}>
                        <Collapse.Panel header={t('DEFAULT_JOB_QUESTIONS')} key="1">
                            <SectionDescription>
                                {t('DEFAULT_JOB_QUESTIONS_DESC')}
                            </SectionDescription>
                            <CompanyDefaultJobExtensionFieldsForm />
                        </Collapse.Panel>
                    </StyledCollapse>
                )}

                {/* --- Default Stage Mappings Section --- */}
                {canManageSettings && canSetDefaultStageMappings && (
                    <StyledCollapse bordered={false} ghost defaultActiveKey={['2']}>
                        <Collapse.Panel header={t('ats.DEFAULT_MAPPINGS_TITLE')} key="2">
                            <SectionDescription>
                                {t('ats.DEFAULT_MAPPINGS_DESCRIPTION')}
                            </SectionDescription>

                            {stageMappingResult.loading ? (
                                <div style={{
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    padding: '2rem'
                                }}>
                                    <Spin size="large" />
                                </div>
                            ) : !stageMappingResult.atsConnected ? (
                                <div style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    padding: '2rem',
                                    backgroundColor: '#ffffff',
                                    border: '1px solid #dae5ec',
                                    borderRadius: '8px'
                                }}>
                                    <p style={{
                                        fontSize: '16px',
                                        color: '#637381',
                                        textAlign: 'center'
                                    }}>
                                        {t('ats.NO_ATS_CONNECTED')}
                                    </p>
                                </div>
                            ) : stageMappingResult.noJobsConnected ? (
                                <div style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    padding: '2rem',
                                    backgroundColor: '#ffffff',
                                    border: '1px solid #dae5ec',
                                    borderRadius: '8px'
                                }}>
                                    <p style={{
                                        fontSize: '16px',
                                        color: '#637381',
                                        textAlign: 'center'
                                    }}>
                                        {t('ats.NO_STAGES_FOUND')}
                                    </p>
                                </div>
                            ) : stageMappingResult.originalStages.length === 0 ? (
                                <div style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    padding: '2rem',
                                    backgroundColor: '#ffffff',
                                    border: '1px solid #dae5ec',
                                    borderRadius: '8px'
                                }}>
                                    <p style={{
                                        fontSize: '16px',
                                        color: '#637381',
                                        textAlign: 'center'
                                    }}>
                                        {t('ats.NO_STAGES_FOUND')}
                                    </p>
                                </div>
                            ) : (
                                <>
                                    {/* --- Top Controls Row --- */}
                                    <ControlsRow>
                                        {/* Search input */}
                                        <SearchWrapper style={{ marginBottom: 0 }}>
                                            <SearchInput
                                                placeholder={t('ats.SEARCH_STAGES')}
                                                value={stageMappingResult.searchQuery}
                                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => stageMappingResult.setSearchQuery(e.target.value)}
                                            />
                                        </SearchWrapper>

                                        {/* Buttons section moved inside ControlsRow */}
                                        <ButtonsContainer style={{ marginTop: 0, marginBottom: 0 }}>
                                            <Button
                                                onClick={stageMappingResult.handleResetChanges}
                                                disabled={stageMappingResult.addedCount === 0 && stageMappingResult.updatedCount === 0 && stageMappingResult.unmappedCount === 0}
                                            >
                                                {t('RESET')}
                                            </Button>
                                            <Button
                                                type="primary"
                                                onClick={stageMappingResult.handleSaveConfirm}
                                                loading={stageMappingResult.saving}
                                                disabled={stageMappingResult.addedCount === 0 && stageMappingResult.updatedCount === 0 && stageMappingResult.unmappedCount === 0}
                                            >
                                                {t('SAVE_MAPPINGS')}
                                            </Button>
                                        </ButtonsContainer>
                                    </ControlsRow>

                                    {/* Action bar for bulk operations */}
                                    <ActionToolbar
                                        selectedCount={stageMappingResult.selectedCount}
                                        onApplyStageType={stageMappingResult.applyStageTypeToSelected}
                                        onClearSelection={stageMappingResult.clearSelection}
                                    />

                                    {/* Stage groups */}
                                    {stageMappingResult.filteredGroups.length === 0 ? (
                                        <Empty
                                            description={<span style={{ color: '#888', fontSize: '13px' }}>{t('ats.NO_SEARCH_RESULTS')}</span>}
                                            image={Empty.PRESENTED_IMAGE_SIMPLE}
                                            style={{ margin: '40px 0' }}
                                        />
                                    ) : (
                                        <DragDropContext onDragEnd={stageMappingResult.handleDragEnd}>
                                            {stageMappingResult.filteredGroups
                                                .map((group, groupIndex) => (
                                                    <StageGroup
                                                        key={group.stageType}
                                                        stageType={group.stageType}
                                                        label={group.label}
                                                        stages={group.stages}
                                                        isExpanded={group.isExpanded}
                                                        selectedStages={stageMappingResult.selectedStages}
                                                        onToggleExpand={() => stageMappingResult.toggleGroupExpansion(groupIndex)}
                                                        onToggleSelection={stageMappingResult.toggleStageSelection}
                                                        onToggleAlwaysUnmap={stageMappingResult.toggleAlwaysUnmap}
                                                    />
                                                ))}
                                        </DragDropContext>
                                    )}
                                </>
                            )}
                        </Collapse.Panel>
                    </StyledCollapse>
                )}

                {/* Permissions Section */}
                {canViewSettings && (
                    <>
                        <SectionTitle>{t('PERMISSIONS')}</SectionTitle>
                        <DropdownContainer>
                            <FlexSelectWrapper
                                suffixIcon={<CaretDownOutlined style={{ color: '#000' }} />}
                                placeholder={t('SELECT_SYSTEM_ROLE')}
                                onChange={handleRoleChange}
                                value={selectedRole}
                            >
                                {(() => {
                                    return roles.map((role) => (
                                        <Select.Option key={role.id} value={role.id.toString()}>
                                            {role.name.charAt(0).toUpperCase() + role.name.slice(1).toLowerCase()}
                                        </Select.Option>
                                    ));
                                })()}
                            </FlexSelectWrapper>
                        </DropdownContainer>

                        <StyledForm
                            scrollToFirstError={true}
                            layout="vertical"
                            name="permissions"
                            form={form}
                        >
                            <PermissionSection>
                                <PermissionTextWrapper>
                                    <PermissionTitle>{t('MASTER_CONTRACTS')}</PermissionTitle>
                                    <PermissionDescription>
                                        {t('CONTROL_ACCESS_TO_MASTER_CONTRACTS')}
                                    </PermissionDescription>
                                </PermissionTextWrapper>
                                <PermissionControls>
                                    <PermissionButton isActive={masterContractLevel === 'None'} onClick={() => { setMasterContractLevel('None'); savePermissions({ masterContractLevel: 'None' }); }}>{t('NONE')}</PermissionButton>
                                    <PermissionButton isActive={masterContractLevel === 'View'} onClick={() => { setMasterContractLevel('View'); savePermissions({ masterContractLevel: 'View' }); }}>{t('VIEW')}</PermissionButton>
                                    <PermissionButton isActive={masterContractLevel === 'Manage'} onClick={() => { setMasterContractLevel('Manage'); savePermissions({ masterContractLevel: 'Manage' }); }}>{t('MANAGE')}</PermissionButton>
                                </PermissionControls>
                            </PermissionSection>

                            <PermissionSection>
                                <PermissionTextWrapper>
                                    <PermissionTitle>{t('AGENCIES')}</PermissionTitle>
                                    <PermissionDescription>
                                        {t('ASSIGN_AND_OR_MANAGE_AGENCY_RELATIONSHIPS')}
                                    </PermissionDescription>
                                </PermissionTextWrapper>
                                <PermissionControls>
                                    <PermissionButton isActive={agenciesLevel === 'None'} onClick={() => { setAgenciesLevel('None'); savePermissions({ agenciesLevel: 'None' }); }}>{t('NONE')}</PermissionButton>
                                    <PermissionButton isActive={agenciesLevel === 'Assign'} onClick={() => { setAgenciesLevel('Assign'); savePermissions({ agenciesLevel: 'Assign' }); }}>{t('ASSIGN')}</PermissionButton>
                                    <PermissionButton isActive={agenciesLevel === 'Manage'} onClick={() => { setAgenciesLevel('Manage'); savePermissions({ agenciesLevel: 'Manage' }); }}>{t('MANAGE')}</PermissionButton>
                                </PermissionControls>
                            </PermissionSection>

                            <PermissionSection>
                                <PermissionTextWrapper>
                                    <PermissionTitle>{t('CHAT_WITH_AGENCY')}</PermissionTitle>
                                    <PermissionDescription>
                                        {t('COMMUNICATION_PERMISSIONS_WITH_AGENCIES')}
                                    </PermissionDescription>
                                </PermissionTextWrapper>
                                <PermissionControls>
                                    <PermissionButton isActive={chatLevel === 'None'} onClick={() => { setChatLevel('None'); savePermissions({ chatLevel: 'None' }); }}>{t('NONE')}</PermissionButton>
                                    <PermissionButton isActive={chatLevel === 'Interact'} onClick={() => { setChatLevel('Interact'); savePermissions({ chatLevel: 'Interact' }); }}>{t('INTERACT')}</PermissionButton>
                                </PermissionControls>
                            </PermissionSection>

                            {/* <PermissionSection>
                                <PermissionTextWrapper>
                                    <PermissionTitle>{t('ANALYTICS')}</PermissionTitle>
                                    <PermissionDescription>
                                        {t('ACCESS_TO_ANALYTICS_AND_REPORTING')}
                                    </PermissionDescription>
                                </PermissionTextWrapper>
                                <PermissionControls>
                                    <PermissionButton isActive={analyticsLevel === 'None'} onClick={() => { setAnalyticsLevel('None'); savePermissions({ analyticsLevel: 'None' }); }}>{t('NONE')}</PermissionButton>
                                    <PermissionButton isActive={analyticsLevel === 'View'} onClick={() => { setAnalyticsLevel('View'); savePermissions({ analyticsLevel: 'View' }); }}>{t('VIEW')}</PermissionButton>
                                </PermissionControls>
                            </PermissionSection> */}
                        </StyledForm>
                    </>
                )}
            </FormWrapper>
        </PageWrapper>
    );
};

export default PermissionsSettings;
